import { supabase } from './supabase-client';

export interface TokenBalance {
  user_id: string;
  token_balance: number;
  created_at: string;
  updated_at: string;
}

export interface TokenTransaction {
  id: number;
  user_id: string;
  transaction_type: 'purchase' | 'deduction' | 'refund' | 'bonus';
  token_amount: number;
  balance_after: number;
  related_session_id?: string;
  stripe_order_id?: string;
  description: string;
  created_at: string;
}

export async function getUserTokenBalance(): Promise<TokenBalance | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('user_tokens')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching token balance:', error);
    throw error;
  }

  return data;
}

export async function checkSufficientTokens(tokensRequired: number): Promise<{
  sufficient: boolean;
  currentBalance: number;
  shortfall: number;
}> {
  const balance = await getUserTokenBalance();
  const currentBalance = balance?.token_balance || 0;
  const sufficient = currentBalance >= tokensRequired;
  const shortfall = sufficient ? 0 : tokensRequired - currentBalance;

  return {
    sufficient,
    currentBalance,
    shortfall
  };
}

export async function deductTokens(
  tokenAmount: number,
  sessionId: string,
  description: string
): Promise<{ success: boolean; newBalance: number }> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const balance = await getUserTokenBalance();
  if (!balance) {
    throw new Error('Token balance not found');
  }

  if (balance.token_balance < tokenAmount) {
    throw new Error('Insufficient tokens');
  }

  const newBalance = balance.token_balance - tokenAmount;

  const { error: updateError } = await supabase
    .from('user_tokens')
    .update({ token_balance: newBalance })
    .eq('user_id', user.id);

  if (updateError) {
    console.error('Error updating token balance:', updateError);
    throw updateError;
  }

  const { error: transactionError } = await supabase
    .from('token_transactions')
    .insert({
      user_id: user.id,
      transaction_type: 'deduction',
      token_amount: -tokenAmount,
      balance_after: newBalance,
      related_session_id: sessionId,
      description
    });

  if (transactionError) {
    console.error('Error logging transaction:', transactionError);
  }

  return {
    success: true,
    newBalance
  };
}

export async function addTokens(
  tokenAmount: number,
  description: string,
  stripeOrderId?: string
): Promise<{ success: boolean; newBalance: number }> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const balance = await getUserTokenBalance();
  if (!balance) {
    throw new Error('Token balance not found');
  }

  const newBalance = balance.token_balance + tokenAmount;

  const { error: updateError } = await supabase
    .from('user_tokens')
    .update({ token_balance: newBalance })
    .eq('user_id', user.id);

  if (updateError) {
    console.error('Error updating token balance:', updateError);
    throw updateError;
  }

  const { error: transactionError } = await supabase
    .from('token_transactions')
    .insert({
      user_id: user.id,
      transaction_type: 'purchase',
      token_amount: tokenAmount,
      balance_after: newBalance,
      stripe_order_id: stripeOrderId,
      description
    });

  if (transactionError) {
    console.error('Error logging transaction:', transactionError);
  }

  return {
    success: true,
    newBalance
  };
}

export async function getTokenTransactionHistory(limit: number = 50): Promise<TokenTransaction[]> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('token_transactions')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching transaction history:', error);
    throw error;
  }

  return data || [];
}

export async function refundTokens(
  tokenAmount: number,
  sessionId: string,
  reason: string
): Promise<{ success: boolean; newBalance: number }> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const balance = await getUserTokenBalance();
  if (!balance) {
    throw new Error('Token balance not found');
  }

  const newBalance = balance.token_balance + tokenAmount;

  const { error: updateError } = await supabase
    .from('user_tokens')
    .update({ token_balance: newBalance })
    .eq('user_id', user.id);

  if (updateError) {
    console.error('Error updating token balance:', updateError);
    throw updateError;
  }

  const { error: transactionError } = await supabase
    .from('token_transactions')
    .insert({
      user_id: user.id,
      transaction_type: 'refund',
      token_amount: tokenAmount,
      balance_after: newBalance,
      related_session_id: sessionId,
      description: `Refund: ${reason}`
    });

  if (transactionError) {
    console.error('Error logging transaction:', transactionError);
  }

  return {
    success: true,
    newBalance
  };
}
